import type { Object3D } from 'three'
import type { EntitySelectionMode } from './EntitySelectionMode'
import {
  ENTITY_SELECTION_CANDIDATE_MASK,
  ENTITY_SELECTION_FEATURE_CANDIDATE_MASK,
  isEntitySelectionCandidateMaskAllowed,
  resolveEntitySelectionCandidateMask,
} from './utils/entitySelectionCandidateMask'

/**
 * 实体选择查询候选分区器。
 *
 * 设计说明：
 * 1. 候选对象的运行时类型在对象生命周期内稳定，只在首次遇到时执行完整守卫；
 * 2. 每次 Hover 只读取缓存位并把对象写入复用数组，不再用多轮 `filter()` 创建中间数组；
 * 3. 显隐、预览替换与选择模式仍由每次查询实时判定，不缓存动态资格；
 * 4. 分区器不拥有场景对象或 GPU 资源，dispose 只解除数组和 WeakMap 引用。
 */
export class EntitySelectionQueryPartitioner {
  /** 对象稳定类型位缓存；WeakMap 不阻止已离场对象回收。 */
  private classificationByObject = new WeakMap<Object3D, EntitySelectionCandidateClassification>()
  /** 跨查询复用的唯一输出分区。 */
  private readonly partitionResult: EntitySelectionCandidatePartition = {
    sketchMeshCandidates: [],
    featureMeshCandidates: [],
    sketchLineCandidates: [],
    featureLineCandidates: [],
    sketchPointCandidates: [],
    featurePointCandidates: [],
  }
  /** 累计查询次数。 */
  private queries = 0
  /** 累计候选访问数。 */
  private candidateVisits = 0
  /** 稳定分类缓存未命中数。 */
  private classificationCacheMisses = 0
  /** 稳定分类缓存命中数。 */
  private classificationCacheHits = 0
  /** 生命周期状态。 */
  private disposed = false

  /**
   * 把候选一次遍历写入当前模式需要的各拾取域。
   *
   * @param candidates 已通过动态显隐资格检查的实体候选。
   * @param selectionMode 本次查询模式。
   * @param isFeatureCandidateBlocked 特征预览替换源的实时屏蔽规则。
   * @returns 分区器拥有的复用结果；调用方必须在本次同步查询内消费。
   */
  public partition(
    candidates: readonly Object3D[],
    selectionMode: EntitySelectionMode,
    isFeatureCandidateBlocked: (object: Object3D) => boolean,
  ): EntitySelectionCandidatePartition {
    this.clearResult()
    if (this.disposed) return this.partitionResult

    this.queries += 1
    this.candidateVisits += candidates.length
    for (const object of candidates) {
      let classification = this.classificationByObject.get(object)
      if (!classification) {
        // 冷态守卫同时建立掩码和具体类型引用，热态不重复守卫，也不做强制类型断言。
        classification = { mask: 0 }
        classification.mask = resolveEntitySelectionCandidateMask(object, classification)
        this.classificationByObject.set(object, classification)
        this.classificationCacheMisses += 1
      } else {
        this.classificationCacheHits += 1
      }
      const mask = classification.mask

      // 预览源资格是命令态动态事实；同一查询只计算一次并在点、线、面域复用。
      const featureCandidateBlocked = (mask & ENTITY_SELECTION_FEATURE_CANDIDATE_MASK) !== 0
        && isFeatureCandidateBlocked(object)
      if (
        !featureCandidateBlocked
        && (mask & ENTITY_SELECTION_CANDIDATE_MASK.FeatureMesh) !== 0
      ) {
        // edge 模式仍依赖实体面确定当前前景边界，所以该域不能被直接模式资格裁掉。
        this.partitionResult.featureMeshCandidates.push(object)
      }
      if (!isEntitySelectionCandidateMaskAllowed(mask, selectionMode)) continue

      if ((mask & ENTITY_SELECTION_CANDIDATE_MASK.SketchMesh) !== 0) {
        this.partitionResult.sketchMeshCandidates.push(object)
      }
      if (classification.sketchLine) {
        this.partitionResult.sketchLineCandidates.push(classification.sketchLine)
      }
      if (!featureCandidateBlocked && classification.featureLine) {
        this.partitionResult.featureLineCandidates.push(classification.featureLine)
      }
      if (classification.sketchPoint) {
        this.partitionResult.sketchPointCandidates.push(classification.sketchPoint)
      }
      if (!featureCandidateBlocked && classification.featurePoint) {
        this.partitionResult.featurePointCandidates.push(classification.featurePoint)
      }
    }
    return this.partitionResult
  }

  /** @returns 只在显式性能检查时创建的分类缓存统计。 */
  public getStats(): EntitySelectionQueryPartitionStats {
    return {
      queries: this.queries,
      candidateVisits: this.candidateVisits,
      classificationCacheMisses: this.classificationCacheMisses,
      classificationCacheHits: this.classificationCacheHits,
    }
  }

  /** 清空候选引用与稳定分类缓存；不销毁场景对象。 */
  public dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.clearResult()
    this.classificationByObject = new WeakMap<Object3D, EntitySelectionCandidateClassification>()
  }

  /** 清空跨查询复用数组，不创建替代数组。 */
  private clearResult(): void {
    this.partitionResult.sketchMeshCandidates.length = 0
    this.partitionResult.featureMeshCandidates.length = 0
    this.partitionResult.sketchLineCandidates.length = 0
    this.partitionResult.featureLineCandidates.length = 0
    this.partitionResult.sketchPointCandidates.length = 0
    this.partitionResult.featurePointCandidates.length = 0
  }
}
